import { Flex, Text } from "@chakra-ui/react";
import { byLook } from "theme/look";
import { LegacyPieChart } from "./legacy/PieChart";
import { isPopulatedArray, toNumberDisplayValue } from "@intentsify/utils";
import { defineChart } from "@tanstack/charts";
import { tooltip } from "@tanstack/charts/tooltip";
import { ErrorBoundary } from "components";
import { Loader } from "components/Loader";
import { NoData } from "components/NoData";
import { useCallback, useMemo, useState } from "react";
import { BrandChart, ChartExportSvg, useBrandChartTheme } from "../BrandChart";
import { MIN_HEIGHT } from "../Charts.const";
import { DonutLegend, formatShare } from "./DonutLegend";
import {
  buildDonutDefinition,
  DonutSlice,
  getDonutSlices,
} from "./PieChart.definition";
import { PieChartItem } from "./PieChart.types";
import { PieColor } from "./PieChart.utils";

/** Donut diameter; fixed so wide cards don't produce oversized charts. */
const DONUT_SIZE = 200;
const DONUT_SIZE_LARGE = 320;
const LARGE_MIN_HEIGHT = 400;

export type PieChartProps = {
  isLoading?: boolean;
  /** @deprecated Kept for call-site compatibility; layout is automatic. */
  margins?: { t: number; b: number; r: number; l: number };
  stretch?: boolean;
  /** @deprecated Kept for call-site compatibility; the donut has a fixed size. */
  fixedRatio?: [width: number, height: number];
  noDataMessage?: string;
  data: PieChartItem[];
  legendPosition?: "bottom" | "right";
  id?: string;
  exportMode?: true;
  title?: string;
  minHeight?: string;
  shouldSort?: boolean;
  displayAsPercent?: boolean;
  colorSet?: { light: PieColor[]; dark: PieColor[] };
};

const BrandPieChart = ({
  isLoading,
  stretch,
  noDataMessage,
  data,
  id,
  title,
  legendPosition = "bottom",
  exportMode = undefined,
  minHeight = MIN_HEIGHT,
  shouldSort = true,
  displayAsPercent = false,
  colorSet = undefined,
}: PieChartProps) => {
  const brandTheme = useBrandChartTheme();
  const [hiddenLabels, setHiddenLabels] = useState<string[]>([]);

  const slices = useMemo(
    () =>
      getDonutSlices(data, brandTheme, {
        shouldSort,
        colors: colorSet?.[brandTheme.mode].map((color) => color.value),
      }),
    [data, brandTheme, shouldSort, colorSet],
  );

  const definition = useMemo(
    () => buildDonutDefinition(slices, { brandTheme, hiddenLabels }),
    [slices, brandTheme, hiddenLabels],
  );

  const interactiveDefinition = useMemo(
    () => defineChart(definition, { tooltip: { use: tooltip } }),
    [definition],
  );

  const buildExportDefinition = useCallback(
    () => buildDonutDefinition(slices, { brandTheme, withLegend: true }),
    [slices, brandTheme],
  );

  const toggleLabel = useCallback((label: string) => {
    setHiddenLabels((hidden) =>
      hidden.includes(label)
        ? hidden.filter((item) => item !== label)
        : [...hidden, label],
    );
  }, []);

  if (isLoading) {
    return <Loader minHeight={minHeight} stretch={stretch} />;
  }

  if (!isPopulatedArray(data)) {
    return (
      <NoData message={noDataMessage} height={minHeight} stretch={stretch} />
    );
  }

  const ariaLabel = title ?? "Breakdown chart";

  if (exportMode) {
    return (
      <ChartExportSvg
        id={id}
        title={title}
        ariaLabel={ariaLabel}
        brandTheme={brandTheme}
        buildDefinition={buildExportDefinition}
      />
    );
  }

  const visibleTotal = slices
    .filter((slice) => !hiddenLabels.includes(slice.label))
    .reduce((sum, slice) => sum + slice.value, 0);
  const size =
    parseInt(minHeight, 10) >= LARGE_MIN_HEIGHT ? DONUT_SIZE_LARGE : DONUT_SIZE;

  return (
    <ErrorBoundary>
      <Flex
        w="100%"
        h="100%"
        gap={5}
        py={2}
        flexDirection={legendPosition === "right" ? "row" : "column"}
        alignItems="center"
        // A bottom legend always starts at the top, so donuts in neighbouring
        // cards line up however long their legends are.
        justifyContent={
          stretch && legendPosition === "right" ? "center" : "flex-start"
        }
      >
        <BrandChart<DonutSlice>
          definition={interactiveDefinition}
          brandTheme={brandTheme}
          ariaLabel={ariaLabel}
          width={`${size}px`}
          height={`${size}px`}
          flexShrink={0}
          renderTooltipBody={({ points }) => {
            const slice = points[0]?.datum;

            if (!slice) {
              return null;
            }

            const share = visibleTotal > 0 ? slice.value / visibleTotal : 0;

            return (
              <Text fontSize="sm">
                <strong>{slice.label}:</strong>{" "}
                {displayAsPercent
                  ? formatShare(share)
                  : `${toNumberDisplayValue(slice.value)} (${formatShare(share)})`}
              </Text>
            );
          }}
        />

        <Flex
          w={legendPosition === "right" ? "280px" : "100%"}
          maxW="100%"
          alignSelf={legendPosition === "right" ? "center" : "stretch"}
        >
          <DonutLegend
            slices={slices}
            hiddenLabels={hiddenLabels}
            onToggle={toggleLabel}
            displayAsPercent={displayAsPercent}
          />
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};

const PieChart = byLook(BrandPieChart, LegacyPieChart);

export { PieChart };
